import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import { UnitFormDto, UnitFormInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-form.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import type { SerdeDtoMetadataInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-dto-metadata.interface';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { UnitService } from '@zaoblako/web/core/services/finance/unit.service';
import { DirectoryCommonHistoryComponent } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/directory-common-history.component';
import { DirectoryCommonHistoryTypeEnum } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/enums/directory-common-history-type.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ColorPickerComponent } from '@zaoblako/web/shared/dumbs/color-picker/color-picker.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { TagModule } from 'primeng/tag';
import { Observable } from 'rxjs';

interface UnitFormExtended extends UnitFormInterface {
  deleted: boolean;
}

@Component({
  standalone: true,
  selector: 'crm-unit-form',
  templateUrl: './unit-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    FormsModule,
    TagModule,
    ButtonModule,
    SpinnerComponent,
    DirectoryCommonHistoryComponent,
    ColorPickerComponent,
    CheckboxModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UnitFormComponent extends BaseFormComponent<UnitFormExtended> implements OnInit {
  @Input() public readonly index: number;
  public unit: UnitFormExtended = {
    name: '',
    short: '',
    color: '',
    deleted: false
  };

  public dto: SerdeDtoClass<UnitFormExtended> = UnitFormDto as any;
  public metadata: SerdeDtoMetadataInterface<UnitFormExtended> =
    UnitFormDto.getMetadata() as SerdeDtoMetadataInterface<UnitFormExtended>;

  public engine: 'class-transformer' | 'serde' = 'serde';
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public canUploadAtrium = false;

  public get DirectoryCommonHistoryTypeEnum() {
    return DirectoryCommonHistoryTypeEnum;
  }

  public constructor(
    public readonly unitService: UnitService,
    public readonly formStoreService: FormStoreService,
    private readonly globalService: GlobalService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit() {
    this.canUploadAtrium = this.globalService.globalInfo?.tenantInfo?.isConnectedToAtrium;

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.unitService.findById<UnitFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
      next: (unit) => {
        this.unit = unit;
        this.formStoreService.setLoading(false, this.index);
        this.cdRef.markForCheck();
      }
    });
  }

  public saveUnit() {
    if (this.checkValidate(this.unit).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.unitService.update<UnitFindInterface, UnitFindInterface>(this.unit).subscribe({
          next: (unit) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Бизнес единица "${this.unit.name}" обновлена`);
            this.formStoreService.saveNext(unit, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.unitService.create<UnitFindInterface>(this.unit).subscribe({
          next: (unit) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Бизнес единица создана');
            this.formStoreService.saveNext(unit, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteUnit() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить бизнес единицу "${this.unit.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.unitService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Бизнес единица "${this.unit.name}" удалена`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public restoreUnit() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить бизнес единицу "${this.unit.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.unitService.restore(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.unit = { ...this.unit, deleted: false };
            this.cdRef.markForCheck();
            this.errorService.addSuccessMessage(`Бизнес единица "${this.unit.name}" восстановлена`);
            this.formStoreService.saveNext(this.unit, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public changeColor(color: string) {
    if (color) {
      this.unit.color = color[0] !== '#' ? '#' + color : color;
    } else {
      this.unit.color = '#ff0000';
    }
  }
}
